import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, AlertTriangle, HelpCircle, Info, Loader2 } from 'lucide-react';

export type ModalVariant = 'default' | 'danger' | 'info';

export interface ModalField {
  name: string;
  label: string;
  placeholder?: string;
  defaultValue?: string;
  pattern?: RegExp;
  patternMessage?: string;
  required?: boolean;
  help?: string;
  autoFocus?: boolean;
}

interface BaseModalProps {
  open: boolean;
  title: string;
  description?: string;
  variant?: ModalVariant;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: (values: Record<string, string>) => void | Promise<void>;
  onCancel: () => void;
  fields?: ModalField[];
  isSubmitting?: boolean;
  children?: React.ReactNode;
}

const variantClasses: Record<ModalVariant, { icon: React.ElementType; iconWrap: string; button: string }> = {
  default: {
    icon: HelpCircle,
    iconWrap: 'bg-lavender/10 text-lavender',
    button: 'bg-lavender text-white hover:bg-lavender-deep',
  },
  danger: {
    icon: AlertTriangle,
    iconWrap: 'bg-red-100 text-red-600',
    button: 'bg-red-600 text-white hover:bg-red-700',
  },
  info: {
    icon: Info,
    iconWrap: 'bg-sky-100 text-sky-600',
    button: 'bg-lavender text-white hover:bg-lavender-deep',
  },
};

const Modal: React.FC<BaseModalProps> = ({
  open,
  title,
  description,
  variant = 'default',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  fields,
  isSubmitting = false,
  children,
}) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const initial: Record<string, string> = {};
    (fields || []).forEach((f) => {
      initial[f.name] = f.defaultValue || '';
    });
    setValues(initial);
    setErrors({});
  }, [open, fields]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (firstFieldRef.current) firstFieldRef.current.focus();
    }, 20);
    return () => clearTimeout(t);
  }, [open]);

  const validate = useCallback(
    (vals: Record<string, string>) => {
      const errs: Record<string, string> = {};
      (fields || []).forEach((f) => {
        const v = (vals[f.name] || '').trim();
        if (f.required && !v) {
          errs[f.name] = `${f.label} is required`;
          return;
        }
        if (v && f.pattern && !f.pattern.test(v)) {
          errs[f.name] = f.patternMessage || `${f.label} is not valid`;
        }
      });
      return errs;
    },
    [fields]
  );

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      const errs = validate(values);
      setErrors(errs);
      if (Object.keys(errs).length > 0) return;
      await onConfirm(values);
    },
    [values, validate, onConfirm]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && !isSubmitting) {
        ev.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isSubmitting, onCancel]);

  if (!open) return null;

  const v = variantClasses[variant];
  const Icon = v.icon;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => !isSubmitting && onCancel()}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="smart-start-modal-title"
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-[fadeIn_0.15s_ease-out]"
      >
        <div className="flex items-start gap-3 p-5 border-b border-gray-100">
          <div className={`p-2 rounded-xl flex-shrink-0 ${v.iconWrap}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="smart-start-modal-title" className="text-lg font-bold text-gray-900 leading-tight">
              {title}
            </h2>
            {description && (
              <p className="text-sm text-gray-600 mt-1">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => !isSubmitting && onCancel()}
            disabled={isSubmitting}
            className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {(fields && fields.length > 0) || children ? (
            <div className="p-5 space-y-4">
              {fields?.map((f, idx) => (
                <div key={f.name}>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                    {f.label}
                    {f.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    ref={idx === 0 || f.autoFocus ? firstFieldRef : undefined}
                    type="text"
                    value={values[f.name] ?? ''}
                    onChange={(e) =>
                      setValues((prev) => ({ ...prev, [f.name]: e.target.value }))
                    }
                    placeholder={f.placeholder}
                    disabled={isSubmitting}
                    className={`w-full p-2.5 text-sm rounded-lg border focus:outline-none focus:ring-2 transition disabled:bg-gray-50 ${
                      errors[f.name]
                        ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                        : 'border-gray-300 focus:border-lavender focus:ring-lavender/20'
                    }`}
                  />
                  {errors[f.name] ? (
                    <p className="text-xs text-red-600 mt-1">{errors[f.name]}</p>
                  ) : f.help ? (
                    <p className="text-xs text-gray-500 mt-1">{f.help}</p>
                  ) : null}
                </div>
              ))}
              {children}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 p-5 bg-gray-50 border-t border-gray-100">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed ${v.button}`}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface ConfirmOptions {
  title: string;
  description?: string;
  variant?: ModalVariant;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface PromptOptions extends ConfirmOptions {
  fields: ModalField[];
}

interface ModalState {
  open: boolean;
  title: string;
  description?: string;
  variant: ModalVariant;
  confirmLabel: string;
  cancelLabel: string;
  fields?: ModalField[];
  resolve?: (value: any) => void;
}

const initialState: ModalState = {
  open: false,
  title: '',
  variant: 'default',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
};

export function useSmartStartModal() {
  const [state, setState] = useState<ModalState>(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const close = useCallback((result: any) => {
    setState((s) => {
      if (s.resolve) s.resolve(result);
      return { ...initialState };
    });
    setIsSubmitting(false);
  }, []);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setState({
        open: true,
        title: opts.title,
        description: opts.description,
        variant: opts.variant || 'default',
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel || 'Cancel',
        resolve,
      });
    });
  }, []);

  const prompt = useCallback(
    (opts: PromptOptions): Promise<Record<string, string> | null> => {
      return new Promise<Record<string, string> | null>((resolve) => {
        setState({
          open: true,
          title: opts.title,
          description: opts.description,
          variant: opts.variant || 'default',
          confirmLabel: opts.confirmLabel || 'OK',
          cancelLabel: opts.cancelLabel || 'Cancel',
          fields: opts.fields,
          resolve,
        });
      });
    },
    []
  );

  const handleConfirm = useCallback(
    (values: Record<string, string>) => {
      if (state.fields && state.fields.length > 0) {
        close(values);
      } else {
        close(true);
      }
    },
    [state.fields, close]
  );

  const handleCancel = useCallback(() => {
    if (state.fields && state.fields.length > 0) {
      close(null);
    } else {
      close(false);
    }
  }, [state.fields, close]);

  const modal = (
    <Modal
      open={state.open}
      title={state.title}
      description={state.description}
      variant={state.variant}
      confirmLabel={state.confirmLabel}
      cancelLabel={state.cancelLabel}
      fields={state.fields}
      isSubmitting={isSubmitting}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { modal, confirm, prompt };
}

export default Modal;
